import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { Locale } from "lib/Intl";
import {
  StyledGenderLink,
  StyledGenderList,
  StyledGenderListItem,
  StyledGenderMenu,
} from "./GenderSelect.styled";
import { useLingui } from "@lingui/react";
import { shopTypeTranslation } from "src/translations/shopTypeTranslation";
import getConfig from "next/config";
import {
  useShopTypeContext,
  useShopTypeDispatch,
} from "src/hooks/context/use-shop-type-context";
import classNames from "classnames";
import Cookies from "js-cookie";
import { OT_SHOP_TYPE_COOKIE } from "src/utils/constants";

// Helper to exclude kids store in specific stores
const excludedShopType: Record<string, string[]> = {
  "OT-UK": ["kids"],
};

const shopTypes = ["women", "men", "kids"];

const { publicRuntimeConfig } = getConfig();

type GenderSelectOptionProps = {
  genderIdValue: string;
  locale: Locale;
  isInHeader: boolean;
};

function GenderSelectOption({
  genderIdValue,
  locale,
  isInHeader,
}: GenderSelectOptionProps) {
  const { currentShopType } = useShopTypeContext();
  const shopTypeDispatch = useShopTypeDispatch();
  const isClientRef = useRef(!!currentShopType);
  const { _ } = useLingui();
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!isClientRef.current) {
      isClientRef.current = true;
    }

    if (currentShopType) {
      setIsActive(currentShopType === genderIdValue);
    }
  }, [currentShopType, genderIdValue]);

  const handleClick = useCallback(() => {
    Cookies.set(OT_SHOP_TYPE_COOKIE, genderIdValue);

    shopTypeDispatch({
      type: "SET_SHOP_TYPE",
      payload: genderIdValue,
    });
  }, [genderIdValue, shopTypeDispatch]);

  return (
    <StyledGenderListItem key={genderIdValue} isInHeader={isInHeader}>
      <StyledGenderLink
        href={getI18nRoute({
          route: `/${genderIdValue}`,
          locale,
        })}
        onClick={handleClick}
        data-testid={`${
          isInHeader ? "main-header" : "nav-menu"
        }-${genderIdValue}`}
        className={classNames({
          active: isActive,
          "is-in-header": isInHeader,
        })}
      >
        <span>{_(shopTypeTranslation[genderIdValue])}</span>
      </StyledGenderLink>
    </StyledGenderListItem>
  );
}

const GenderSelect = forwardRef<
  HTMLDivElement,
  { locale: Locale; isInHeader?: boolean }
>(({ locale, isInHeader = false }, ref) => {
  const filteredShopTypes = useMemo(
    () =>
      shopTypes.filter(
        (shopType) =>
          !excludedShopType[publicRuntimeConfig.STORE_KEY || "OT-NL"]?.includes(
            shopType
          )
      ),
    []
  );

  return (
    <StyledGenderMenu
      ref={ref}
      isInHeader={isInHeader}
      data-testid="navmenu-links"
      aria-labelledby="gender-select-label"
    >
      <p id="gender-select-label" className="sr-only">
        Gender
      </p>
      <StyledGenderList isInHeader={isInHeader}>
        {filteredShopTypes.map((genderIdValue) => (
          <GenderSelectOption
            key={genderIdValue}
            genderIdValue={genderIdValue}
            locale={locale}
            isInHeader={isInHeader}
          />
        ))}
      </StyledGenderList>
    </StyledGenderMenu>
  );
});

GenderSelect.displayName = "GenderSelect";

export { GenderSelect };
